import type { AssetDisplayState, AssetKind } from "./meetingAssets";
import { assetState } from "./meetingAssets";

/** What the player placeholder talks about: any of screen, camera or audio. */
export type MessageSubject = AssetKind | "recording";

export type AssetMessage = {
  key: string;
  args: { asset: string };
};

/**
 * The one sentence every surface uses for an asset that is not available: the
 * meeting page's panels, the player placeholder and the asset bar menus. `asset`
 * is an i18n key for the lower-case name, translated by the caller alongside the
 * message.
 *
 * `none` says only that no file of that kind exists. It no longer claims the
 * meeting "was not recorded": a meeting with audio and no camera video was
 * recorded.
 */
export const assetMessage = (
  subject: MessageSubject,
  state: AssetDisplayState,
): AssetMessage | null =>
  state === "available"
    ? null
    : {
        key: `resources.zoom_meetings.asset_message.${state}`,
        args: { asset: `resources.zoom_meetings.assets_lower.${subject}` },
      };

type MediaRow = Parameters<typeof assetState>[0];

// Most hopeful first: something still moving outranks something that failed, so
// a meeting whose screen upload failed but whose audio is uploading reads
// "uploading" rather than "failed".
const MEDIA_ORDER: AssetDisplayState[] = [
  "available",
  "processing",
  "waiting",
  "failed",
  "none",
];

/**
 * Screen, camera and audio folded into one state, for the player placeholder,
 * which stands for all three.
 */
export const mediaState = (
  row: MediaRow | undefined,
  now: number = Date.now(),
): AssetDisplayState => {
  if (!row) return "none";
  const states = (["screen", "camera", "audio"] as const).map((kind) =>
    assetState(row, kind, now),
  );
  return MEDIA_ORDER.find((s) => states.includes(s)) ?? "none";
};
